import axios from 'axios'
import clsx from 'clsx'
import {ChangeEvent, Dispatch, DragEvent, FC, SetStateAction, useEffect, useMemo, useRef, useState} from 'react'
import {useAuth} from '../../../app/modules/auth'
import {API_URL} from '../crud-helper/helpers'
import {normalizeUploadedFile, UploadedFile} from './uploadTypes'
import {FieldHelpLabel} from './FieldHelp'

export type UploaderProps = {
  label?: string
  maxFiles?: number
  maxFileSize?: string
  allowMultiple?: boolean
  name?: string
  setFiles?: Dispatch<SetStateAction<UploadedFile[]>>
  files?: UploadedFile[]
  acceptedFileTypes?: string[] | undefined
  hasDownload?: boolean
  onBusyChange?: (busy: boolean) => void
}

type UploadQueueItem = {
  id: string
  name: string
  progress: number
}

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch (error) {
    return value
  }
}

const uploadUrl = (source: string) => {
  if (/^https?:\/\//i.test(source)) {
    return source
  }

  if (source.startsWith('/api/')) {
    return `${API_URL}${source.substring(4)}`
  }

  if (source.startsWith('/ticketing/') || source.startsWith('/file-library/')) {
    return `${API_URL}${source}`
  }

  return `${API_URL}/upload?load=${encodeURIComponent(source)}`
}

const formatBytes = (value?: number) => {
  if (!value || value <= 0) {
    return ''
  }

  const units = ['B', 'KB', 'MB', 'GB']
  let size = value
  let unit = 0

  while (size >= 1024 && unit < units.length - 1) {
    size = size / 1024
    unit += 1
  }

  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

const parseMaxFileSize = (value?: string) => {
  if (!value) {
    return 0
  }

  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*(b|kb|mb|gb)?$/i)
  if (!match) {
    return 0
  }

  const number = Number(match[1])
  const unit = (match[2] || 'b').toLowerCase()
  const multipliers: Record<string, number> = {
    b: 1,
    kb: 1024,
    mb: 1024 * 1024,
    gb: 1024 * 1024 * 1024,
  }

  return Math.round(number * (multipliers[unit] || 1))
}

const fileNameFromSource = (source: string) => {
  const sourcePath = source.split('?')[0].split('#')[0]
  return safeDecodeURIComponent(sourcePath.split('/').filter(Boolean).pop() || 'attachment')
}

const isFileAccepted = (file: File, acceptedFileTypes?: string[]) => {
  if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
    return true
  }

  const fileName = file.name.toLowerCase()
  const fileType = (file.type || '').toLowerCase()

  return acceptedFileTypes.some((accepted) => {
    const value = accepted.toLowerCase()
    if (value.endsWith('/*')) {
      return fileType.startsWith(value.slice(0, -1))
    }
    if (value.startsWith('.')) {
      return fileName.endsWith(value)
    }
    return fileType === value
  })
}

const isImageFile = (file: UploadedFile) => {
  const name = file.options?.file?.name || fileNameFromSource((file.source || '').toString())
  const type = file.options?.file?.type || ''
  return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
}

const Uploader: FC<UploaderProps> = ({
  label = 'فایل‌ها را اینجا رها کنید یا انتخاب کنید',
  maxFiles = 10,
  maxFileSize = '30MB',
  allowMultiple = false,
  name,
  setFiles,
  files = [],
  acceptedFileTypes,
  hasDownload,
  onBusyChange,
}) => {
  const {currentUser} = useAuth()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [queue, setQueue] = useState<UploadQueueItem[]>([])
  const maxBytes = useMemo(() => parseMaxFileSize(maxFileSize), [maxFileSize])
  const busy = queue.length > 0

  useEffect(() => {
    onBusyChange?.(busy)
  }, [busy, onBusyChange])

  const addUploadedFile = (uploadedFile: UploadedFile) => {
    if (!setFiles) {
      return
    }

    const normalized = normalizeUploadedFile(uploadedFile)
    setFiles((oldFiles) => {
      const currentFiles = oldFiles || []
      const nextFiles = allowMultiple
        ? [...currentFiles.filter((item) => item.source !== normalized.source), normalized]
        : [normalized]

      return nextFiles.slice(Math.max(0, nextFiles.length - maxFiles))
    })
  }

  const removeUploadedFile = (source: string) => {
    if (!setFiles) {
      return
    }

    setFiles((oldFiles) => (oldFiles || []).filter((item) => item.source !== source))
  }

  const setUploadProgress = (id: string, progress: number) => {
    setQueue((items) => items.map((item) => (item.id === id ? {...item, progress} : item)))
  }

  const finishUpload = (id: string) => {
    setQueue((items) => items.filter((item) => item.id !== id))
  }

  const uploadOne = async (file: File) => {
    const id = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
    setQueue((items) => [...items, {id, name: file.name, progress: 0}])

    try {
      const formData = new FormData()
      formData.append(name || 'file', file)

      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          ...(currentUser?.tk ? {Authorization: 'Bearer ' + currentUser.tk} : {}),
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!event.total) {
            return
          }
          setUploadProgress(id, Math.round((event.loaded / event.total) * 100))
        },
      })

      addUploadedFile(response.data as UploadedFile)
      setUploadError('')
    } catch (error: any) {
      setUploadError(error?.response?.data?.message || 'آپلود فایل با خطا مواجه شد.')
    } finally {
      finishUpload(id)
    }
  }

  const uploadFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length || busy) {
      return
    }

    setUploadError('')

    const existingCount = allowMultiple ? files.length : 0
    const remainingSlots = Math.max(0, maxFiles - existingCount)
    const limitedFiles = allowMultiple ? selectedFiles.slice(0, remainingSlots) : selectedFiles.slice(0, 1)

    if (limitedFiles.length < selectedFiles.length) {
      setUploadError(`حداکثر ${maxFiles} فایل قابل انتخاب است.`)
    }

    const validFiles = limitedFiles.filter((file) => {
      if (maxBytes > 0 && file.size > maxBytes) {
        setUploadError(`حجم ${file.name} بیشتر از ${maxFileSize} است.`)
        return false
      }

      if (!isFileAccepted(file, acceptedFileTypes)) {
        setUploadError(`نوع فایل ${file.name} مجاز نیست.`)
        return false
      }

      return true
    })

    for (const file of validFiles) {
      await uploadOne(file)
    }
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    uploadFiles(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDragging(false)
    uploadFiles(Array.from(event.dataTransfer.files || []))
  }

  return (
    <div className='w-100'>
      <div
        className={clsx(
          'border border-dashed rounded px-4 py-5 bg-light text-center',
          dragging ? 'border-primary bg-light-primary' : 'border-gray-300',
          busy && 'opacity-75'
        )}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          name={name}
          type='file'
          className='d-none'
          multiple={allowMultiple}
          accept={acceptedFileTypes?.join(',')}
          disabled={busy}
          onChange={handleInputChange}
        />

        <button
          type='button'
          className='btn btn-sm btn-light-primary mb-3'
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <i className='las la-cloud-upload-alt fs-2 me-1'></i>
          انتخاب فایل
        </button>

        <div className='fw-bold text-gray-700'>
          <FieldHelpLabel label={label} />
        </div>
        <div className='text-muted fs-8 mt-1'>
          {allowMultiple ? `حداکثر ${maxFiles} فایل` : 'یک فایل'}، حداکثر {maxFileSize}
        </div>
      </div>

      {uploadError && <div className='alert alert-danger py-2 px-3 mt-3 mb-0'>{uploadError}</div>}

      {queue.length > 0 && (
        <div className='d-flex flex-column gap-2 mt-3'>
          {queue.map((item) => (
            <div key={item.id} className='border rounded px-3 py-2 bg-white'>
              <div className='d-flex justify-content-between fs-8 mb-1'>
                <span className='text-truncate pe-3'>{item.name}</span>
                <span className='text-muted'>{item.progress}%</span>
              </div>
              <div className='progress h-6px'>
                <div className='progress-bar bg-primary' style={{width: `${item.progress}%`}} />
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className='d-flex flex-column gap-2 mt-3'>
          {files.map((file, index) => {
            const source = file.source?.toString() || ''
            const meta = file.options?.file
            const fileName = meta?.name || fileNameFromSource(source)
            const fileSize = formatBytes(meta?.size)
            const url = uploadUrl(source)

            return (
              <div
                key={`${source}-${index}`}
                className='d-flex align-items-center justify-content-between border rounded px-3 py-2 bg-white'
                style={{minHeight: 54}}
              >
                <div className='d-flex align-items-center overflow-hidden'>
                  {isImageFile(file) ? (
                    <img
                      alt={fileName}
                      src={url}
                      className='rounded border me-3'
                      style={{width: 40, height: 40, objectFit: 'cover'}}
                    />
                  ) : (
                    <i className='las la-paperclip fs-2x text-primary me-3'></i>
                  )}
                  <div className='overflow-hidden'>
                    <div className='fw-bold text-gray-800 text-truncate' title={fileName}>
                      {fileName}
                    </div>
                    {fileSize && <div className='text-muted fs-8'>{fileSize}</div>}
                  </div>
                </div>
                <div className='d-flex align-items-center gap-2 ms-3'>
                  {hasDownload && (
                    <a
                      href={url}
                      target='_blank'
                      rel='noreferrer'
                      download
                      className='btn btn-sm btn-icon btn-light-primary'
                      title='دانلود'
                    >
                      <i className='las la-download fs-2'></i>
                    </a>
                  )}
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-danger'
                    onClick={() => removeUploadedFile(source)}
                    title='حذف'
                    disabled={busy}
                  >
                    <i className='las la-trash fs-2'></i>
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export {Uploader}
